import React, { useContext, useState } from "react";
import { useTranslation } from 'react-i18next';
import "./modern-style.css";
import "../../../styles/page-titles.css";
import "react-toastify/dist/ReactToastify.css";

import { useMemo } from 'react';

import { formatDateTz } from "../../../utils/timezone";
import { formatPrice } from "../../../utils/formatPrice";
import { formatPriceWithSymbol, getCurrencySymbol } from "../../../utils/currency";
import useGetCaixa from "../../../hooks/useGetCaixa";

import { calcularDiferencasCaixa } from "../../../utils/caixaCalculations";

import { SidebarContext } from "../../../AppRoutes";
import PermissionGate from "../../../services/PermissionGate";

import { IoMdOpen } from "react-icons/io";
import { FiCalendar, FiFilter, FiClock, FiArrowLeft } from "react-icons/fi";
import { IoWalletOutline } from "react-icons/io5";
import { HiOutlineReceiptTax } from "react-icons/hi";
import { useNavigate } from "react-router-dom";

import DatePicker from "react-datepicker";
import { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ptBR from "date-fns/locale/pt-BR";
import enUS from "date-fns/locale/en-US";
import styled from "styled-components"

import CustomTable from '../../../components/CustomTable';
import regionConfig from "../../../config/regionConfig";

registerLocale("pt-BR", ptBR);
registerLocale("en-US", enUS);

// Locale e formato do calendario seguem a regiao ativa (BR: pt-BR / LB: en-US)
const DATE_PICKER_LOCALE = regionConfig.locale.default;
const DATE_PICKER_FORMAT = regionConfig.locale.dateFormat
  .replace(/DD/g, "dd")
  .replace(/YYYY/g, "yyyy");

const ContainerCaixa = styled.div`
  width: auto;
  min-height: 100vh;
  background: #ffffff;
  margin-left: ${({ sidebar }) => (sidebar ? '250px' : '100px')};
  padding: 24px;
  transition: margin-left 200ms ease;
  display: flex;
  flex-direction: column;

  @media (max-width: 880px) {
    margin-left: 0;
    padding: 16px;
  }
`


function CaixaHistory() {
  const { t } = useTranslation('cashRegister');
  const { sidebar } = useContext(SidebarContext);
  const empresa = localStorage.getItem("empresa");
  const empresaParse = JSON.parse(empresa);
  const empresaId = empresaParse._id;

  const navigate = useNavigate();
  const [page, setPage] = useState(1);

  const [startDate, setStartDate] = useState(() => {
    const today = new Date();
    const futureDate = new Date(today.setDate(today.getDate() - 15));
    return futureDate;
  });

  const [endDate, setEndDate] = useState(new Date());

  const { data: caixasResponse } = useGetCaixa(
    empresaId,
    page,
    startDate,
    endDate
  );

  const columns = [
    {
      title: t('history.columns.opening'),
      dataIndex: 'abertura',
      key: 'abertura',
      sorter: true,
    },
    {
      title: t('history.columns.initialBalance'),
      dataIndex: 'initial_value',
      key: 'initial_value',
      render: (value) => <span className="modern-value-neutral">{value}</span>,
    },
    {
      title: t('history.columns.finalBalance'),
      dataIndex: 'final_value',
      key: 'final_value',
      render: (value) => <span className="modern-value-positive">{value}</span>,
    },
    {
      title: t('history.columns.closing'),
      dataIndex: 'closed_at',
      key: 'closed_at',
      responsive: ['lg'],
    },
    {
      title: t('history.columns.entries'),
      dataIndex: 'positive_values',
      key: 'positive_values',
      responsive: ['md'],
      render: (value) => <span className="modern-value-positive">{value}</span>,
    },
    {
      title: t('history.columns.withdrawals'),
      dataIndex: 'negative_values',
      key: 'negative_values',
      responsive: ['lg'],
      render: (value) => <span className="modern-value-negative">{value}</span>,
    },
    {
      title: t('history.columns.calculatedDifference'),
      dataIndex: 'diferenca_total',
      key: 'diferenca_total',
      responsive: ['md'],
      render: (value, record) => {
        if (record.diferenca_total_raw === undefined || record.diferenca_total_raw === null) {
          return <span className="modern-value-neutral">-</span>;
        }
        const isPositive = record.diferenca_total_raw > 0;
        const isZero = record.diferenca_total_raw === 0;
        return (
          <span className={`modern-value-${isZero ? 'neutral' : isPositive ? 'positive' : 'negative'}`}>
            {value}
          </span>
        );
      },
    },
    {
      title: t('history.columns.actions'),
      dataIndex: 'actions',
      key: 'actions',
      width: 80,
      render: (url) => (
        <button
          className="modern-action-btn"
          onClick={() => navigate(url)}
          title={t('history.viewDetails')}
        >
          <IoMdOpen />
        </button>
      ),
    },
  ];

  const tableData = useMemo(() => {
    if(!caixasResponse?.data?.caixas) return [];

    return caixasResponse?.data?.caixas.map((item) => {
      return {
        key: item._id,
        abertura: formatDateTz(item.data_abertura, regionConfig.locale.dateTimeFormat),
        initial_value: formatPriceWithSymbol(item.saldo_inicial),
        final_value: formatPriceWithSymbol(item.saldo_final),
        closed_at: formatDateTz(item.data_fechamento, regionConfig.locale.dateTimeFormat),
        positive_values: formatPriceWithSymbol(
          item.lancamentos_caixa.reduce(
            (acc, item) => (item.valor >= 0 ? acc + item.valor : acc),
            0
          )
        ),
        negative_values: formatPriceWithSymbol(
          item.lancamentos_caixa.reduce(
            (acc, item) => (item.valor < 0 ? acc + item.valor : acc),
            0
          )
        ),
        diferenca_total: (() => {
          // Calcular diferença dinamicamente se os valores existem
          if (item.valor_esperado_dinheiro !== undefined ||
              item.valor_esperado_cartao !== undefined ||
              item.valor_esperado_pix !== undefined) {
            const diferencas = calcularDiferencasCaixa(item);
            return `${getCurrencySymbol()} ${diferencas.diferenca_total >= 0 ? '+' : ''}${formatPrice(diferencas.diferenca_total)}`;
          }
          // Fallback para caixas antigos
          return item.diferenca_total !== undefined && item.diferenca_total !== null
            ? `${getCurrencySymbol()} ${item.diferenca_total >= 0 ? '+' : ''}${formatPrice(item.diferenca_total)}`
            : '-';
        })(),
        diferenca_total_raw: (() => {
          // Calcular diferença dinamicamente se os valores existem
          if (item.valor_esperado_dinheiro !== undefined ||
              item.valor_esperado_cartao !== undefined ||
              item.valor_esperado_pix !== undefined) {
            const diferencas = calcularDiferencasCaixa(item);
            return diferencas.diferenca_total;
          }
          // Fallback para caixas antigos
          return item.diferenca_total;
        })(),
        actions: `/caixa/historico/${item._id}`,
      };
    })
  }, [caixasResponse])



  return (
    <>
      <PermissionGate permissions={["default"]}>
        <ContainerCaixa sidebar={sidebar}>
          <div className="modern-history-container">
            {/* Header Section */}
            <div className="modern-history-header">
              <div className="modern-history-title">
                <div className="page-title-with-icon">
                  <div className="page-icon"><IoWalletOutline /></div>
                  <h1 className="page-title">{t('history.title')}</h1>
                </div>
              </div>
              <button
                className="modern-back-btn"
                onClick={() => navigate('/caixa')}
                title={t('history.backToRegister')}
                style={{marginRight: '100px'}}
              >
                <FiArrowLeft />
                {t('history.back')}
              </button>
            </div>

            {/* Filters Section */}
            <div className="modern-filters-container">
              <h3 className="modern-filters-title">
                <FiFilter />
                {t('history.filtersTitle')}
              </h3>
              <div className="modern-filters-grid">
                <div className="modern-date-group">
                  <label className="modern-date-label">
                    <FiCalendar /> {t('history.startDate')}
                  </label>
                  <div className="modern-date-picker">
                    <DatePicker
                      selected={startDate}
                      locale={DATE_PICKER_LOCALE}
                      dateFormat={DATE_PICKER_FORMAT}
                      onChange={(date) => setStartDate(date)}
                      placeholderText={t('history.startDatePlaceholder')}
                    />
                  </div>
                </div>
                <div className="modern-date-group">
                  <label className="modern-date-label">
                    <FiCalendar /> {t('history.endDate')}
                  </label>
                  <div className="modern-date-picker">
                    <DatePicker
                      selected={endDate}
                      locale={DATE_PICKER_LOCALE}
                      dateFormat={DATE_PICKER_FORMAT}
                      onChange={(date) => setEndDate(date)}
                      placeholderText={t('history.endDatePlaceholder')}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Table Section */}
            <div className="modern-history-table-container">
              <div className="modern-history-table-header">
                <h3>
                  <FiClock />
                  {t('history.tableTitle')}
                </h3>
                <span className="modern-history-count">
                  {t('history.count', { count: caixasResponse?.data?.totalCaixas || 0 })}
                </span>
              </div>
                                      <CustomTable
                className="modern-history-table"
                columns={columns}
                dataSource={tableData}
                onChange={(pagination) => setPage(pagination.current)}
                pagination={{
                  current: page,
                  pageSize: 10,
                  total: caixasResponse?.data?.totalCaixas || 0,
                  showSizeChanger: false,
                  showQuickJumper: true,
                  showTotal: (total, range) => t('history.paginationRange', { from: range[0], to: range[1], total })
                }}
                locale={{
                  emptyText: (
                    <div className="modern-empty-state">
                      <HiOutlineReceiptTax className="modern-empty-icon" />
                      <div className="modern-empty-title">{t('history.emptyTitle')}</div>
                      <div className="modern-empty-description">
                        {t('history.emptyDescription')}
                      </div>
                    </div>
                  )
                }}
              />

            </div>
          </div>
        </ContainerCaixa>
      </PermissionGate>
    </>
  );
}

export default CaixaHistory;
